import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GroupAnalyticsFilters } from '@/components/filter/FilterItems';
import { api } from '@/redux/api';
import { Call } from '@/types/call';
import { Unit } from '@/types/unit';
import { UnitAnalyticsSummary } from '@/types/unit-analytics-summary';
import { Response } from '@/types/wellbeing-response';
import { unitPassesFilters } from '@/utils/apply-group-analytics-filters';
import { computeUnitAnalytics } from '@/utils/compute-unit-analytics';

type UnitRawData = {
  unitId: number;
  calls: Call[];
  responses: Response[];
};

export function isAnyFilterActive(filters: GroupAnalyticsFilters): boolean {
  return (
    filters.missedCalls !== 'all' ||
    filters.callEndedBy !== 'all' ||
    filters.callDuration !== 'all' ||
    filters.callsMade !== 'all' ||
    filters.notificationsSent !== 'all' ||
    filters.announcementsSent !== 'all' ||
    filters.notificationResponses !== 'all' ||
    filters.dateRange.start !== undefined ||
    filters.dateRange.end !== undefined
  );
}

export function useGroupAnalytics(
  units: Unit[],
  filters: GroupAnalyticsFilters,
) {
  const [triggerGetCalls] = api.useLazyGetCallsQuery();
  const [triggerGetResponses] = api.useLazyGetResponsesQuery();

  const [rawData, setRawData] = useState<Map<number, UnitRawData>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [hasFetched, setHasFetched] = useState(false);

  const filtersActive = isAnyFilterActive(filters);

  const unitIds = useMemo(
    () =>
      units
        .map((u) => u.id)
        .sort()
        .join(','),
    [units],
  );

  const unitsRef = useRef(units);
  unitsRef.current = units;

  const fetchAllData = useCallback(async () => {
    const currentUnits = unitsRef.current;
    if (currentUnits.length === 0) {
      setRawData(new Map());
      setIsLoading(false);
      setLoadedCount(0);
      return;
    }

    setIsLoading(true);
    setLoadedCount(0);
    const newData = new Map<number, UnitRawData>();

    const promises = currentUnits.map(async (unit) => {
      const [callsResult, responsesResult, announcementsResult] =
        await Promise.allSettled([
          triggerGetCalls({ unitId: unit.id }).unwrap(),
          triggerGetResponses({
            unitId: unit.id,
            type: 'responseNotification',
          }).unwrap(),
          triggerGetResponses({
            unitId: unit.id,
            type: 'announcement',
          }).unwrap(),
        ]);

      const calls = callsResult.status === 'fulfilled' ? callsResult.value : [];
      const responses =
        responsesResult.status === 'fulfilled' ? responsesResult.value : [];
      const announcements =
        announcementsResult.status === 'fulfilled'
          ? announcementsResult.value
          : [];

      newData.set(unit.id, {
        unitId: unit.id,
        calls,
        responses: [...responses, ...announcements],
      });
      setLoadedCount((prev) => prev + 1);
    });

    await Promise.allSettled(promises);
    setRawData(newData);
    setIsLoading(false);
    setHasFetched(true);
  }, [triggerGetCalls, triggerGetResponses]);

  // Lazy loading: only fetch when filters become active
  useEffect(() => {
    if (filtersActive && !hasFetched) {
      fetchAllData();
    }
  }, [filtersActive, hasFetched, fetchAllData]);

  // Re-fetch if unit list changes while filters are active
  useEffect(() => {
    if (filtersActive && hasFetched) {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitIds]);

  const summaries = useMemo(() => {
    const map = new Map<number, UnitAnalyticsSummary>();
    for (const [unitId, data] of rawData) {
      map.set(
        unitId,
        computeUnitAnalytics(
          unitId,
          data.calls,
          data.responses,
          filters.dateRange,
        ),
      );
    }
    return map;
  }, [rawData, filters.dateRange]);

  const filteredUnitIds = useMemo(() => {
    const ids = new Set<number>();
    for (const [unitId, summary] of summaries) {
      if (unitPassesFilters(summary, filters)) {
        ids.add(unitId);
      }
    }
    return ids;
  }, [summaries, filters]);

  return {
    summaries,
    filteredUnitIds,
    isLoading,
    loadedCount,
    totalCount: units.length,
    filtersActive,
    hasFetched,
    refetch: fetchAllData,
  };
}
